import Microgame from '../engine/Microgame.js';

export default class TraceGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Trace !`, en: `Trace!`, es: `¡Traza!` },
            controls: { fr: `Glisse ton doigt/souris pour suivre le chemin indiqué`, en: `Swipe to follow the indicated path`, es: `Desliza para seguir el camino indicado` },
            objective: { fr: `Repasse exactement sur la forme lumineuse`, en: `Trace exactly over the glowing shape`, es: `Traza exactamente sobre la forma brillante` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" stroke-width="2"><path d="m3 17 6-6M9 11l6 6M15 17l6-6"/><circle cx="3" cy="17" r="1"/><circle cx="21" cy="11" r="1"/></svg>`,
            actions: [
                { type: 'swipe', fromX: 10, fromY: 35, toX: 35, toY: 45, duration: 240 },
                { type: 'swipe', fromX: 35, fromY: 45, toX: 60, toY: 55, duration: 240 },
                { type: 'swipe', fromX: 60, fromY: 55, toX: 90, toY: 60, duration: 240 },
                { type: 'wait', duration: 420 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.svg = null;
        this.path = null;
        this.cursor = null;
        this.targetPoint = null;
        this.isTracing = false;
        this.progress = 0;
        this.pathLength = 0;
        this.tolerance = 45; // Generous tolerance for complex paths

        const speedMult = engine.speedMultiplier || 1.0;
        // Equivalent to 3.5 speed at max 4.0
        const adjustedSpeed = 1 + (speedMult - 1) * (2.5 / 3);
        this.duration = 3000 * (speedMult / adjustedSpeed);
    }

    setup() {
        this.engine.ui.instruction.textContent = "TRACE!";

        // Generate Random Path (Cubic Bezier for more variety)
        const w = this.container.clientWidth;
        const h = this.container.clientHeight;

        // Random Start (Left side, padded)
        const startX = w * 0.1;
        const startY = h * 0.2 + Math.random() * (h * 0.6); // 20% to 80% height

        // Random End (Right side, padded)
        const endX = w * 0.9;
        const endY = h * 0.2 + Math.random() * (h * 0.6);

        // Control Point 1 (Left-ish middle)
        const cp1X = w * (0.2 + Math.random() * 0.3); // 20-50% width
        const cp1Y = Math.random() * h; // Any height

        // Control Point 2 (Right-ish middle)
        const cp2X = w * (0.5 + Math.random() * 0.3); // 50-80% width
        const cp2Y = Math.random() * h; // Any height

        // Construct Cubic Bezier Path
        const d = `M ${startX} ${startY} C ${cp1X} ${cp1Y}, ${cp2X} ${cp2Y}, ${endX} ${endY}`;

        // SVG Container
        this.svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        this.svg.setAttribute("width", "100%");
        this.svg.setAttribute("height", "100%");
        this.svg.style.position = "absolute";
        this.svg.style.top = "0";
        this.svg.style.left = "0";
        this.container.appendChild(this.svg);

        // Guide Path (Background)
        const guide = document.createElementNS("http://www.w3.org/2000/svg", "path");
        guide.setAttribute("d", d);
        guide.setAttribute("stroke", "#333");
        guide.setAttribute("stroke-width", "20");
        guide.setAttribute("fill", "none");
        guide.setAttribute("stroke-linecap", "round");
        this.svg.appendChild(guide);

        // Active Path (Progress)
        this.path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        this.path.setAttribute("d", d);
        this.path.setAttribute("stroke", this.primaryColor);
        this.path.setAttribute("stroke-width", "20");
        this.path.setAttribute("fill", "none");
        this.path.setAttribute("stroke-linecap", "round");
        this.svg.appendChild(this.path);

        this.pathLength = this.path.getTotalLength();

        // Hide active path initially
        this.path.setAttribute("stroke-dasharray", this.pathLength);
        this.path.setAttribute("stroke-dashoffset", this.pathLength);

        // Target Point (The dot to follow)
        this.targetPoint = document.createElement('div');
        this.targetPoint.style.position = 'absolute';
        this.targetPoint.style.width = '40px';
        this.targetPoint.style.height = '40px';
        this.targetPoint.style.background = this.secondaryColor;
        this.targetPoint.style.borderRadius = '50%';
        this.targetPoint.style.boxShadow = `0 0 10px ${this.secondaryColor}`;
        this.targetPoint.style.transform = 'translate(-50%, -50%)';
        this.targetPoint.style.pointerEvents = 'none'; // Let input pass through
        this.container.appendChild(this.targetPoint);

        this.updateTargetPos(0);

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    updateTargetPos(prog) {
        const p = this.path.getPointAtLength(prog);
        this.targetPoint.style.left = `${p.x}px`;
        this.targetPoint.style.top = `${p.y}px`;

        // Update path visual
        this.path.setAttribute("stroke-dashoffset", this.pathLength - prog);
    }

    onDown(e) {
        if (!this.active) return;

        const rect = this.container.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Check if starting near the beginning
        const startP = this.path.getPointAtLength(0);
        const dist = Math.hypot(x - startP.x, y - startP.y);

        if (dist < 60) { // Generous hit area
            this.isTracing = true;
            this.targetPoint.style.background = '#fff';
        }
    }

    onMove(e) {
        if (!this.active || !this.isTracing) return;

        const rect = this.container.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const currentP = this.path.getPointAtLength(this.progress);
        const dist = Math.hypot(x - currentP.x, y - currentP.y);

        if (dist > this.tolerance) {
            // Failed - went off track
            this.lose();
            this.isTracing = false;
            this.targetPoint.style.background = '#ff0055';
            return;
        }

        // Advance progress
        // Look ahead up to 60px
        let bestProg = this.progress;
        let minD = dist;

        for (let i = 1; i < 60; i++) {
            const checkProg = this.progress + i;
            if (checkProg > this.pathLength) break;

            const p = this.path.getPointAtLength(checkProg);
            const d = Math.hypot(x - p.x, y - p.y);
            if (d < minD) {
                minD = d;
                bestProg = checkProg;
            }
        }

        this.progress = bestProg;
        this.updateTargetPos(this.progress);

        if (this.progress >= this.pathLength - 10) {
            this.win();
        }
    }

    onUp() {
        this.isTracing = false;
        this.targetPoint.style.background = this.secondaryColor;
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
